import React, { useState } from "react";
import { TableBody, TableCell, TableRow } from "@material-ui/core";
import { AVERAGE_MINUTES_IN_MONTH, priceRowType, rankService, termLookup, YearDaysForCalculation } from "../../../utils/appConstants";
import { currencyFormatter, getDateAsExcelFormat, getDateForPriceRow } from "../../../utils/methods";
import TitleSubtitle from "../../DesignSystem/Table/Cells/TitleSubtitle";
import Row from "./row";

interface GroupRowProps {
  priceRows: priceRowType[];
  className?: string;
}
const GroupRow: React.FC<GroupRowProps> = ({ priceRows, className }) => {
  const [open, setOpen] = React.useState(false);

  // Determine the parent/alpha row
  const isAlphaRow = (row1: priceRowType["row"], row2: priceRowType["row"]): boolean => {
    if (!row1?.dates.end && row2?.dates.end) return true;
    return (row1?.priority || 100) < (row2?.priority || 100);
  };
  let alphaRowIndex = 0;
  const companySet = new Set();
  const nodeSet = new Set();
  const locationSet = new Set();
  const descriptionSubtitleSet = new Set<string>();
  let monthlyRecurringSum = 0;
  let minutelyRecurringSum = 0;
  let monthlyRecurringSumEnded = 0;
  let minutelyRecurringSumEnded = 0;
  let hasActive = false;
  priceRows.forEach((obj: priceRowType, index: number) => {
    companySet.add(obj.row?.targetOrg.title);
    nodeSet.add(obj.row?.location.title);
    locationSet.add(obj.row?.location.subtitle);
    descriptionSubtitleSet.add(obj.row?.description.subtitle || " - ");
    if (!obj.row?.dates.end) {
      hasActive = true;
      if (obj.type === "income") {
        if (obj.row?.term.type === "MRC" || obj.row?.term.type === "contract") monthlyRecurringSum += obj.row.term.charge;
        if (obj.row?.term.type === "demand") minutelyRecurringSum += obj.row.term.charge;
      } else if (obj.type === "expense") {
        if (obj.row?.term.type === "MRC" || obj.row?.term.type === "contract") monthlyRecurringSum -= obj.row.term.charge;
        if (obj.row?.term.type === "demand") minutelyRecurringSum -= obj.row.term.charge;
      }
    } else {
      if (obj.type === "income") {
        if (obj.row?.term.type === "MRC" || obj.row?.term.type === "contract") monthlyRecurringSumEnded += obj.row.term.charge;
        if (obj.row?.term.type === "demand") minutelyRecurringSumEnded += obj.row.term.charge;
      } else if (obj.type === "expense") {
        if (obj.row?.term.type === "MRC" || obj.row?.term.type === "contract") monthlyRecurringSumEnded -= obj.row.term.charge;
        if (obj.row?.term.type === "demand") minutelyRecurringSumEnded -= obj.row.term.charge;
      }
    }
    if (isAlphaRow(obj.row, priceRows[alphaRowIndex].row)) alphaRowIndex = index;
  });

  // TERM

  let termColor;
  let termTitle;
  let termSubtitle;
  if (!hasActive) {
    minutelyRecurringSum += minutelyRecurringSumEnded;
    monthlyRecurringSum += monthlyRecurringSumEnded;
  }
  if (monthlyRecurringSum !== 0) {
    minutelyRecurringSum *= AVERAGE_MINUTES_IN_MONTH / 100;
    const termSum = monthlyRecurringSum + minutelyRecurringSum;
    if (termSum > 0) termColor = "income";
    if (termSum < 0) termColor = "expense";
    const isEstimate = minutelyRecurringSum > 0 ? "≅" : "";
    termTitle = `${isEstimate}$${currencyFormatter(Math.abs(termSum), 2)} / month`;
  } else if (minutelyRecurringSum !== 0) {
    if (minutelyRecurringSum > 0) termColor = "income";
    if (minutelyRecurringSum < 0) termColor = "expense";
    termTitle = `${currencyFormatter(Math.abs(minutelyRecurringSum), 2)}¢ / minute`;
    termSubtitle = `≅$${currencyFormatter(Math.abs((minutelyRecurringSum / 100) * AVERAGE_MINUTES_IN_MONTH), 2)} / month`;
  } else {
    termTitle = "No charge";
  }
  if (!hasActive) termColor += "-ended";

  // Make Rows
  const close = () => setOpen(false);
  const rows = priceRows.map((obj: priceRowType, index) => {
    if (index === alphaRowIndex) return <Row className={`${className} background-2`} close={close} priceRow={obj} key={index} />;
    return <Row className={`${className} background-2`} priceRow={obj} key={index} />;
  });
  const alphaRowByIndex: priceRowType | null = alphaRowIndex >= 0 && alphaRowIndex < priceRows.length ? priceRows[alphaRowIndex] : null;
  const alphaRow = rows.splice(alphaRowIndex, 1)[0];
  rows.unshift(alphaRow);
  const companies = Array.from(companySet).join(", ");
  const locationTitle = alphaRowByIndex?.row?.location.title;
  const locationSubtitle = alphaRowByIndex?.row?.location.subtitle;
  const descriptionSubtitle: string = alphaRowByIndex?.row?.description.subtitle || "";

  const getDates = (row: priceRowType["row"]): { title: string; subtitle: string } => {
    if (row?.term.type !== "fee") {
      return {
        title: `${getDateForPriceRow(row?.dates.start || "")}`,
        subtitle: row?.dates.end ? `${getDateForPriceRow(row?.dates.end || "")}` : "-",
      };
    }
    return {
      title: `${getDateForPriceRow(row?.dates.start || "")}`,
      subtitle: "",
    };
  };
  const totalsSum = priceRows
    .map((row: priceRowType) => {
      return row.type === "expense" ? -row.fee - row.mrc : row.fee + row.mrc;
    })
    .reduce((row, val) => row + val, 0);
  const formatTotal = (): string => {
    if (totalsSum < 0) {
      return `( $${currencyFormatter(Math.abs(totalsSum))} )`;
    }
    return `$${currencyFormatter(totalsSum)}`;
  };
  const hasOnDemand = priceRows.findIndex((row: priceRowType) => row.row?.term.type === "demand") >= 0;

  const dateCell = getDates(alphaRowByIndex?.row);
  const [fontWeight, setFontWeight] = useState<"400" | "700">("400");
  // if service date ended, term has subtitle (24 month or month avg)
  if (!priceRows) return null;
  return (
    <>
      {!open && (
        <TableRow
          onClick={() => setOpen(!open)}
          onMouseOver={() => setFontWeight("700")}
          onMouseLeave={() => setFontWeight("400")}
          className={` pointer ${className}`}
        >
          <TableCell onClick={() => setOpen(!open)} style={{ fontSize: "30px", cursor: "pointer", fontWeight: `${fontWeight}` }} width="1%">
            {open ? "-" : "+"}
          </TableCell>
          <TitleSubtitle
            title={alphaRowByIndex?.row?.description.title || ""}
            subTitle={descriptionSubtitle}
            boldTitle
            width="19%"
            maxWidth="19vw"
            align="left"
          />
          <TitleSubtitle title={locationTitle || ""} subTitle={locationSubtitle || ""} boldTitle width="25%" maxWidth="25vw" align="center" />
          <TitleSubtitle title={companies} subTitle={alphaRowByIndex?.row?.targetOrg.subtitle} width="15%" maxWidth="15vw" align="center" />
          <TitleSubtitle outlineColor={termColor} title={termTitle} subTitle={termSubtitle} width="15%" maxWidth="15vw" align="center" />
          <TitleSubtitle
            title={getDateAsExcelFormat(dateCell.title) || ""}
            subTitle={getDateAsExcelFormat(dateCell.subtitle) || ""}
            width="10%"
            maxWidth="10vw"
            align="center"
          />
          <TitleSubtitle title={formatTotal()} boldTitle width="10%" maxWidth="10vw" align="right" />
        </TableRow>
      )}
      {open && rows}
    </>
  );
};

export default GroupRow;
